import React from "react";

// CFT v2: resources read three bands. High/Medium share the teal family (kept
// distinct, as Strong/Developing were); Low is Emerging orange. Same on every surface.
const BAND_COLOR = { High: "#1B8A6B", Medium: "#2F6B7C", Low: "#D78E2C" };
// Small eyebrow text needs AA contrast on white; Low uses a darker orange there.
const EYEBROW_COLOR = { High: "#17775C", Medium: "#2F6B7C", Low: "#9A5B12" };
const GAP_COLOR = "#AD393E";
const ORDER = ["capacity", "focus", "time"];
const oswald = { fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase" };

function Components({ r }) {
  return (
    <ul className="mt-3 mb-3" style={{ listStyle: "none", padding: 0 }} data-testid={`resource-components-${r.key}`}>
      {r.components.map((c) => (
        <li
          key={c.dimension}
          className="flex justify-between text-xs py-1"
          style={{ borderBottom: "1px solid #F0F0F0", color: c.is_lowest ? "#AD393E" : "#1A1A1A", fontWeight: c.is_lowest ? 600 : 400 }}
          data-testid={`resource-component-${r.key}-${c.dimension}`}
        >
          <span>{c.label}{c.is_lowest ? " · lowest" : ""}</span>
          <span style={{ fontFamily: "Oswald" }}>{c.mean_display}</span>
        </li>
      ))}
    </ul>
  );
}

function HighlightRow({ item, color, testid }) {
  return (
    <div data-testid={testid}>
      <div style={{ ...oswald, fontSize: 10.5, color, marginTop: 4 }}>{item.eyebrow}</div>
      <p className="text-xs leading-snug" style={{ color: "#1A1A1A" }}>
        Item {item.item_label} ({item.dimension_label}): “{item.text}” · {item.mean_display}
        {item.reverse ? " (reverse-scored)" : ""}
      </p>
    </div>
  );
}

function Highlights({ h, accent, rkey }) {
  if (!h) return null;
  return (
    <div className="pt-2 mt-2" style={{ borderTop: "1px dashed #DDD" }} data-testid={`resource-${rkey}-highlights`}>
      {h.uniform ? (
        <p className="text-xs" data-testid={`resource-${rkey}-uniform`}>{h.message}</p>
      ) : (
        <>
          <HighlightRow item={h.strength} color={accent} testid={`resource-${rkey}-strength`} />
          <HighlightRow item={h.gap} color={GAP_COLOR} testid={`resource-${rkey}-gap`} />
        </>
      )}
    </div>
  );
}

function ResourceColumn({ r, register }) {
  if (!r || r.suppressed) {
    return (
      <div className="p-5" style={{ background: "#FAFAFA", border: "1px dashed #DDD" }} data-testid={`resource-${r?.key}-suppressed`}>
        <div className="eyebrow mb-2">{r?.label}</div>
        <p className="text-xs text-gray-500">Suppressed — fewer than five respondents in this view.</p>
      </div>
    );
  }
  const color = BAND_COLOR[r.band] || "#555";
  const ins = r.insight;
  return (
    <div
      className="p-5 transition-shadow duration-200 hover:shadow-md"
      style={{ background: "white", border: "1px solid #E0E0E0", borderTop: `4px solid ${color}` }}
      data-testid={`resource-${r.key}`}
    >
      <div className="flex items-baseline justify-between">
        <span style={{ ...oswald, fontSize: 12, color: "#555" }}>{r.label}</span>
        <span style={{ ...oswald, fontSize: 10, color: "white", background: color, padding: "2px 8px" }} data-testid={`resource-${r.key}-band`}>
          {r.band}
        </span>
      </div>
      <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 40, lineHeight: 1.1, color }} data-testid={`resource-${r.key}-score`}>
        {r.score_display}
      </div>
      {r.note && (
        <p className="text-xs mt-2 mb-1 pl-3" style={{ borderLeft: "2px solid #4592A8", color: "#444" }} data-testid={`resource-${r.key}-note`}>
          {r.note}
        </p>
      )}
      <Components r={r} />
      {"spread" in r && (
        <div className="text-xs mb-2" style={{ ...oswald, letterSpacing: "0.08em", color: "#555" }} data-testid={`resource-${r.key}-spread`}>
          Spread (SD) {r.spread_display}
        </div>
      )}
      <div className="space-y-2 text-sm leading-relaxed" data-testid={`resource-${r.key}-insight`}>
        <p style={{ fontWeight: 600 }}>{ins.metric}</p>
        <p>{ins.interpretation}</p>
        {ins.tie_note && <p className="text-xs text-gray-600">{ins.tie_note}</p>}
        <p style={{ color: "#2F6B7C" }}>
          <span style={{ ...oswald, fontSize: 10, marginRight: 6 }}>Where to start</span>
          {ins.action}
        </p>
        <Highlights h={r.highlights} accent={EYEBROW_COLOR[r.band] || color} rkey={r.key} />
        {register === "individual" && r.reflection_question && (
          <p className="italic" style={{ color: "#1A1A1A" }} data-testid={`resource-${r.key}-question`}>
            <span style={{ ...oswald, fontStyle: "normal", fontSize: 10, marginRight: 6, color: "#AD393E" }}>Reflect</span>
            {r.reflection_question}
          </p>
        )}
      </div>
    </div>
  );
}

export default function ResourceSummary({ resources, meta, register = "team" }) {
  if (!resources) return null;
  const cols = ORDER.map((k) => resources[k]);
  if (register === "individual" && cols.every((r) => !r || r.suppressed)) return null;
  return (
    <section className="space-y-4" data-testid="resource-summary">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1" style={{ color: "#4592A8" }}>Capacity, Focus, Time</div>
          <h3 className="text-xl" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
            {register === "team" ? "Three resources, read from the seven dimensions." : "Where your resilience is resourced."}
          </h3>
        </div>
        {meta?.sample_label && (
          <span className="text-xs px-2 py-1" style={{ ...oswald, letterSpacing: "0.1em", color: "#8A6418", background: "#FBF3E2", border: "1px solid #D9A441" }} data-testid="resource-sample-label">
            {meta.sample_label}
          </span>
        )}
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cols.map((r, i) => <ResourceColumn key={ORDER[i]} r={r || { key: ORDER[i], suppressed: true }} register={register} />)}
      </div>
      {meta && (
        <p className="text-xs text-gray-500 max-w-prose" data-testid="resource-footnote">
          {meta.footnote} {meta.disclaimer}
        </p>
      )}
    </section>
  );
}
